import getConfig from 'next/config';
import {
  DOMAIN_PREFIX,
  LANGUAGE_ENUMS,
  urlPrefix,
} from '../../constants/staticPageConstants';
import { cleartaxAxios } from '../../http-common';
import { identifyUserInNetcore } from '../../utils/netcore';
import { B2C_slugs, REGIONS_WITH_NON_ENGLISH_PRIMARY } from './constants';
const { publicRuntimeConfig } = getConfig();

interface ILoginStatus {
  'anon_id': string;
  'external-id': string;
  'isloggedin': boolean;
  'username': string;
}

interface NotificationTypes {
  g: string;
  h: boolean;
  j?: string;
  isNotDisplayed: () => boolean;
  isSkippedMoment: () => boolean;
}

interface LoginResponse {
  clientId: string;
  client_id: string;
  credential: boolean;
  select_by: string;
}

export const userLogout = (): Promise<void> => {
  return cleartaxAxios
    .get('/f/auth/logout')
    .then((res) => res.data)
    .catch((err) => console.log(err));
};

export const checkLoginStatus = async (): Promise<ILoginStatus> => {
  return await cleartaxAxios
    .get(`/f/retcon/me/`)
    .then((res) => res.data)
    .catch((err) => console.log(err));
};

//SPEED-TODO: Update this to be hashmap
export const checkIfB2CPage = (slug: string): boolean =>
  B2C_slugs.find((item) => item === slug) ? true : false;

export const appendOrCreateQueryParam = (
  link: string,
  key: string,
  value: string
): string => {
  if (!link) return link || '';

  const url = new URL(link);
  const hasKey = url?.searchParams.has(key);

  if (!hasKey) return setQueryParamToLink(link, key, value);

  const existingValue = url?.searchParams.get(key);
  const keyValue = `${existingValue}_${value}`;
  url.searchParams.set(key, keyValue);
  return url.toString();
};

export const setQueryParamToLink = (
  link: string,
  key: string,
  value: string
): string => {
  if (!link) return '';

  const baseURL = publicRuntimeConfig.CLEARTAX_DOMAIN;
  const url = new URL(link, baseURL);
  url?.searchParams.append(key, value);
  return url.toString();
};

export const myImageLoader = (logo: string): string => {
  return logo;
};

const addLanguageCode = (link: string, language: LANGUAGE_ENUMS): string => {
  return `${link}/${urlPrefix[language as keyof typeof urlPrefix]}`;
};

export const getLogoLink = (
  domain: number,
  language: LANGUAGE_ENUMS
): string => {
  const link = `https://www.${DOMAIN_PREFIX[domain]?.host}`;
  if (REGIONS_WITH_NON_ENGLISH_PRIMARY.includes(domain)) {
    if (language === LANGUAGE_ENUMS.English)
      return addLanguageCode(link, language);
    else return link;
  } else if (language !== LANGUAGE_ENUMS.English)
    return addLanguageCode(link, language);

  return link;
};

/**
 * Checks if Google Sign-In is supported in the current environment
 * Returns false for WebViews and Google Translate proxy domains
 */
const isGoogleSignInSupported = (): boolean => {
  // Server-side rendering check
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  // Check if running on Google Translate proxy domain
  if (window.location.hostname.includes('translate.goog')) {
    return false;
  }

  const userAgent = navigator.userAgent || '';

  // Android WebView detection
  const isAndroidWebView =
    /Android/.test(userAgent) && /wv|WebView/.test(userAgent);

  // iOS WebView detection (not Safari)
  // Safari, Chrome, Firefox, and Opera all have identifiable strings
  const isIOSWebView =
    /(iPhone|iPod|iPad)/.test(userAgent) &&
    !/(Safari|CriOS|FxiOS|OPiOS|mercury)/.test(userAgent);

  if (isAndroidWebView || isIOSWebView) {
    return false;
  }

  return true;
};

export const OneTabLogin = async (
  setLoggedIn: (status: boolean) => void,
  setUserLoggedIn?: (status: boolean) => void
) => {
  // Skip GSI initialization in unsupported environments
  if (!isGoogleSignInSupported()) {
    console.log(
      '[GSI] Google Sign-In skipped: unsupported environment (WebView or translate.goog)'
    );
    return;
  }

  const script = document.createElement('script');
  const clientId = publicRuntimeConfig.ONE_TAP_CLIENT_ID;
  script.src = 'https://accounts.google.com/gsi/client';

  script.async = true;

  // Handle script loading errors
  script.onerror = (error) => {
    console.error('[GSI] Failed to load Google Sign-In script:', error);
  };

  script.onload = () => {
    try {
      // Null-safe access to Google API
      if (!window?.google?.accounts?.id) {
        console.error('[GSI] Google Sign-In API not available');
        return;
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async function handleCredentialResponse(
          response: LoginResponse
        ) {
          if (response.credential) {
            const id_token = response.credential;
            const client_id = clientId;

            const requestBody = {
              id_token: id_token,
              client_id: client_id,
            };

            const headers = {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            };

            await cleartaxAxios
              .post(`/f/auth/googleonetap`, requestBody, { headers })
              .then((res) => {
                const userEmail = res?.data?.username;
                if (userEmail) {
                  localStorage.setItem('userEmail', userEmail);
                  setLoggedIn(true);
                  identifyUserInNetcore(userEmail);
                  if (setUserLoggedIn) {
                    setUserLoggedIn(true);
                  }
                } else {
                  localStorage.removeItem('userEmail');
                }
              })
              .catch((err) => console.log(err));
          } else {
            throw Error('Err');
          }
        },
        cancel_on_tap_outside: false,
      });

      window.google.accounts.id.prompt((notification: NotificationTypes) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          console.log(notification);
        }
      });
    } catch (error) {
      // Catch any errors from GSI initialization/prompt to prevent unhandled exceptions
      console.error('[GSI] Error during Google Sign-In initialization:', error);
    }
  };

  document.head.appendChild(script);
};

export const CHAT_IFRAME_IDENTIFIER = 'blogPage';
